import { ConsultantContractTableRowDataFragment } from "@graphql";
import { ActionUnion } from "@consultants/consultant-profile-new/contract-tab/types";
import { useSnackbars } from "@hooks";
import {
  CancelIcon,
  CircleIcon,
  Color,
  ContractEditCheckIcon,
  MoreHorizIcon,
  MoreList,
  Table
} from "@bonliva-crm/ui";
import { IconButton, TableCell, TableRow, Tooltip, Typography } from "@material-ui/core";
import cns from "classnames";
import { entries } from "@consultants/consultant-profile-new/contract-tab/components/utils/entries";
import { downloadFileById } from "@api";
import { OpenInNew, SaveAlt } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import locale from "@bonliva-crm/locale";
import moment from "moment";

const headers = [
  {
    width: "30%",
    content: locale.Department
  },
  {
    width: "15%",
    content: locale.City
  },
  {
    width: "20%",
    content: locale.AgreementType
  },
  {
    width: "5%",
    content: locale.Alt
  },
  {
    width: "145px",
    content: locale.Date
  },
  {
    width: "5%",
    content: locale.Payout
  },
  {
    width: "auto",
    content: ""
  }
];

const childHeaders = [locale.Department, locale.City, locale.Contract, locale.Period, ""];

export const ConsultantContractsTable = (props: {
  contracts: ConsultantContractTableRowDataFragment[];
  openContractMoreList: string | null;
  setOpenContractMoreList: (s: string | null) => void;
  setAction: (s: string | null) => void;
  setChosenContract: (c: ConsultantContractTableRowDataFragment) => void;
  handleNavigationAction: (action: ActionUnion, contractId?: string) => void;
  defaultCollapsedState?: boolean;
}) => {
  const classes = useStyles();
  const { showError } = useSnackbars();

  return (
    <Table
      defaultCollapsedState={props.defaultCollapsedState}
      headers={headers}
      childHeaders={childHeaders}
      rows={props.contracts.map((contract) => ({
        cells: [
          contract.CustomerName,
          <Typography variant="body2" noWrap>
            {contract.CustomerCity}
          </Typography>,
          <span className={classes.flexLeft}>
            {locale.consultantContractTypes[contract.Type]}
            {contract.IsSigned ? (
              <ContractEditCheckIcon htmlColor={Color.CheckGreen} />
            ) : undefined}
            {contract.IsNullified && (
              <Tooltip title={contract.NullificationReason || ""}>
                <div>
                  <CancelIcon color="error" />
                </div>
              </Tooltip>
            )}
          </span>,
          <span className={classes.flexCenter}>{contract.SalaryChoiceAbbr}</span>,
          <Tooltip
            title={contract.Period ? `${locale.PeriodBooked}: ${contract.Period}` : ""}
          >
            <span className={cns(classes.width100)}>
              {contract.StartDate && contract.EndDate && (
                <>
                  <div>{`${locale.From}: ${moment(contract.StartDate).format("L")}`}</div>
                  <div>{`${locale.To}: ${moment(contract.EndDate).format("L")}`}</div>
                </>
              )}
            </span>
          </Tooltip>,
          contract.PayoutsAbbr,
          props.openContractMoreList === contract._id ? (
            <MoreList
              key={contract._id}
              clickAway={() => props.setOpenContractMoreList(null)}
              entries={entries({
                closeList: () => props.setOpenContractMoreList(null),
                setAction: (a: string) => props.setAction(a),
                contract: contract,
                handleNavigationAction: props.handleNavigationAction,
                showError: showError
              })}
            />
          ) : undefined,
          () => {
            if (props.openContractMoreList === contract._id) {
              props.setOpenContractMoreList(null);
              return;
            }
            props.setOpenContractMoreList(contract._id);
            props.setChosenContract(contract);
          }
        ],
        children:
          contract?.ChildContracts?.length > 0
            ? contract.ChildContracts.map((c) => [
                <TableRow className={classes.noBorder}>
                  {childHeaders?.map((header, i) => (
                    <TableCell className={classes.childHeaderCell} key={i}>
                      {header}
                    </TableCell>
                  ))}
                  <TableCell
                    className={cns(classes.childHeaderCell, classes.cursorPointer)}
                    onClick={() => {
                      if (props.openContractMoreList === c._id) {
                        props.setOpenContractMoreList(null);
                        return;
                      }
                      props.setOpenContractMoreList(c._id);
                      props.setChosenContract(c);
                    }}
                  >
                    <MoreHorizIcon />
                  </TableCell>
                </TableRow>,
                <TableRow className={classes.noBorder}>
                  <TableCell className={classes.noBorder}>
                    <Typography variant="body2">{c.CustomerName}</Typography>
                  </TableCell>
                  <TableCell className={classes.noBorder}>
                    <Typography variant="body2">{c.CustomerCity}</Typography>
                  </TableCell>
                  <TableCell className={classes.noBorder}>
                    <Typography variant="body2">
                      {locale.consultantContractTypes[c.Type]}
                      {c.IsSigned ? (
                        <ContractEditCheckIcon htmlColor={Color.CheckGreen} />
                      ) : undefined}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.noBorder}>
                    <Typography variant="body2" className={classes.twoRowsOnly}>
                      {c.Period}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.noBorder}>
                    {props.openContractMoreList === c._id ? (
                      <MoreList
                        marginTop={-25}
                        key={c._id}
                        clickAway={() => {
                          props.setOpenContractMoreList(null);
                        }}
                        entries={entries({
                          closeList: () => {
                            props.setOpenContractMoreList(null);
                          },
                          setAction: (a: string | null) => props.setAction(a),
                          handleNavigationAction: props.handleNavigationAction,
                          contract: c,
                          showError: showError
                        })}
                      />
                    ) : undefined}
                  </TableCell>
                </TableRow>
              ])
            : undefined,
        editProofs: contract.editsProof.map((proof) => (
          <div
            style={{
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "row",
                alignItems: "center"
              }}
            >
              <div>
                <Typography variant="body2">{locale.ErrorEdit}</Typography>
                <Typography variant="caption" className={classes.editInfo}>
                  {locale.by}: {proof.editBy.Name}
                  <CircleIcon
                    htmlColor={Color.DetailsInput}
                    style={{ width: "6px", margin: 4 }}
                  />
                  {proof.editOn}
                </Typography>
              </div>
              {proof.fileRefs.length > 0 ? (
                <ContractEditCheckIcon
                  htmlColor={Color.CheckGreen}
                  style={{ marginLeft: 10 }}
                />
              ) : undefined}
            </div>

            {proof.fileRefs.map((fileRef) => (
              <div style={{ display: "flex", alignItems: "center", marginLeft: "20px" }}>
                <Typography variant="body2">{fileRef.Name}</Typography>
                <div style={{ marginLeft: "auto" }}>
                  <IconButton
                    onClick={() => downloadFileById(fileRef.NodeID, fileRef.Name)}
                    size="small"
                  >
                    <SaveAlt />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      window.open(`/shared-files/${fileRef.NodeID}`, "_blank")
                    }
                  >
                    <OpenInNew />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        ))
      }))}
    />
  );
};

const useStyles = makeStyles(() => ({
  noBorder: {
    border: "none !important"
  },
  flexCenter: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  flexLeft: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  },
  width100: {
    width: "100%"
  },
  childHeaderCell: {
    border: "none",
    fontWeight: "bold",
    paddingBottom: "0 !important"
  },
  cursorPointer: {
    cursor: "pointer",
    textAlign: "right"
  },
  editInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingTop: 0,
    border: "none",
    color: Color.DetailsInput,
    textTransform: "capitalize"
  },
  twoRowsOnly: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
    maxWidth: "17.5rem"
  },
  noWrap: {
    whiteSpace: "nowrap"
  }
}));
